/**
 * ═══════════════════════════════════════════════════════════════════════
 * realtimeMiddleware — Zustand Middleware for Event-Driven Sync (Hardened)
 * ═══════════════════════════════════════════════════════════════════════
 *
 * SECURITY:
 *   • Only allowlisted event types are processed (ALLOWED_STORE_EVENTS)
 *   • State snapshots only merge safe data keys (no functions, no loading)
 *   • Payload validation before state application
 *   • Prototype pollution prevention on patch objects
 *
 * Wraps a Zustand store to:
 *   1. Intercept mutations → emit events to EventBus (local source)
 *   2. Listen for remote/cross-tab events → apply state patches
 *   3. Support optimistic UI with rollback on server rejection
 *   4. Track pending optimistic operations for conflict resolution
 *
 * Usage:
 *   const useStore = create(
 *     realtimeMiddleware((set, get) => ({ ... }), {
 *       storeName: 'visit',
 *       eventHandlers: { ... },
 *     })
 *   );
 */

import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import eventBus from '../services/realtime/EventBus';

// ── Security: Keys that should NEVER be set from external events ─────
const BLOCKED_STATE_KEYS = new Set([
  '__proto__', 'constructor', 'prototype',   // prototype pollution
  'loading', 'error', 'successMessage',       // UI-only state
]);

// ── Security: Keys safe to merge from STATE_SNAPSHOT ─────────────────
// Only data lists — not functions, not UI state, not auth state
const SAFE_SNAPSHOT_KEYS = new Set([
  'visits', 'dailyVisits', 'doctors', 'patientLookup', 'createdVisit',
]);

// ── HMR-safe cleanup registry ────────────────────────────────────────
// Stored at MODULE level (not on `api`) so it survives HMR of consumer
// files (e.g. visitStore.js). When visitStore.js HMR-updates, `create()`
// generates a new `api` object, but this Map persists because
// realtimeMiddleware.js itself didn't change.
const _cleanupByStore = new Map();

/**
 * Create a realtime-aware Zustand store config.
 *
 * @param {Function} storeCreator  - Standard Zustand (set, get) => state
 * @param {Object}   options
 * @param {string}   options.storeName     - Unique store name (e.g. 'visit')
 * @param {Object}   options.eventHandlers - { [eventType]: (currentState, event) => partialState }
 */
export function realtimeMiddleware(storeCreator, options = {}) {
  const { storeName = 'unknown', eventHandlers = {} } = options;
  const _debug = process.env.REACT_APP_DEBUG === 'true';

  return (set, get, api) => {
    // ── Wire up event listeners after store creation ──────────────
    const _unsubscribers = [];

    // Use queueMicrotask to ensure the store is fully initialized
    queueMicrotask(() => {
      // Clean up previous listeners for THIS store (HMR safety).
      // Uses module-level Map — survives `create()` giving a new `api` object.
      const prevCleanup = _cleanupByStore.get(storeName);
      if (prevCleanup) {
        prevCleanup();
        if (_debug) console.log(`[RT_DEBUG] HMR cleanup for store "${storeName}" — old listeners removed`);
      }

      Object.entries(eventHandlers).forEach(([eventType, handler]) => {
        const unsub = eventBus.on(eventType, (event) => {
          // Skip events we caused locally (already applied optimistically)
          if (event.source === 'local') return;

          const currentState = get();
          const patch = handler(currentState, event);

          if (patch && typeof patch === 'object' && Object.keys(patch).length > 0) {
            // Security: strip blocked keys from any patch
            const safePatch = {};
            for (const [key, value] of Object.entries(patch)) {
              if (!BLOCKED_STATE_KEYS.has(key) && typeof value !== 'function') {
                safePatch[key] = value;
              }
            }
            if (Object.keys(safePatch).length > 0) {
              if (_debug) console.log('[RT_DEBUG] Middleware patch:', eventType, '→', Object.keys(safePatch));
              set(safePatch, false); // shallow merge, no replace
            }
          }
        });
        _unsubscribers.push(unsub);
      });

      // Listen for state snapshots (cross-tab reconciliation)
      // Security: Only accept snapshots with source 'cross-tab'
      // and only merge SAFE_SNAPSHOT_KEYS (never functions, loading, auth)
      const unsubSnapshot = eventBus.on('__STATE_SNAPSHOT__', (event) => {
        // Reject snapshots from 'remote' source (server can't trigger this)
        if (event.source !== 'cross-tab') return;

        if (event.payload?.slice === storeName) {
          const snapshotState = event.payload.state;
          if (snapshotState && typeof snapshotState === 'object') {
            const patch = {};
            for (const key of Object.keys(snapshotState)) {
              // Only merge allowlisted data keys
              if (
                SAFE_SNAPSHOT_KEYS.has(key) &&
                !BLOCKED_STATE_KEYS.has(key) &&
                typeof snapshotState[key] !== 'function'
              ) {
                patch[key] = snapshotState[key];
              }
            }
            if (Object.keys(patch).length > 0) {
              set(patch, false);
            }
          }
        }
      });
      _unsubscribers.push(unsubSnapshot);

      // Store cleanup function in module-level registry (not on api)
      _cleanupByStore.set(storeName, () => {
        _unsubscribers.forEach((unsub) => unsub());
        _unsubscribers.length = 0;
      });
    });

    // ── Create the original store with enhanced set ──────────────
    return storeCreator(set, get, api);
  };
}

/**
 * Helper: create optimistic update utilities for a store action.
 *
 * Returns { apply, rollback } so the action can:
 *   1. apply()  — immediately update UI (optimistic)
 *   2. await apiCall()
 *   3. On error: rollback() — revert to previous state
 *
 * @param {Function} get      - Zustand get
 * @param {Function} set      - Zustand set
 * @param {Object}   patch    - The optimistic state patch
 * @param {string[]} keys     - Which state keys to snapshot for rollback
 */
export function createOptimisticUpdate(get, set, patch, keys) {
  // Snapshot current state for rollback
  const snapshot = {};
  const currentState = get();
  keys.forEach((key) => {
    snapshot[key] = currentState[key];
  });

  return {
    apply: () => set(patch, false),
    rollback: () => set(snapshot, false),
    snapshot,
  };
}

/**
 * Helper: map a visit status change to the correct event type.
 */
export function statusToEventType(newStatus) {
  switch (newStatus) {
    case 'IN_PROGRESS':
      return REALTIME_EVENTS.QUEUE_STARTED;
    case 'CANCELLED':
      return REALTIME_EVENTS.QUEUE_SKIPPED;
    case 'COMPLETED':
      return REALTIME_EVENTS.VISIT_STATUS_CHANGED;
    case 'SKIPPED':
      return REALTIME_EVENTS.VISIT_SKIPPED;
    default:
      return REALTIME_EVENTS.VISIT_STATUS_CHANGED;
  }
}
